import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { useCategoryTree } from '../hooks/useCategoryTree';
import { CategoryNode, CategoryTreeHistoryItem } from '../types/category';
import { FIELD_LABELS } from '../utils/fieldLabels';
import { formatMetricaDate } from '../utils/dateUtils';
import { downloadCategoryTreeExcel, printCategoryTreePdf } from '../utils/categoryTreeExport';
import {
  addNamedChild,
  ancestorIds,
  cloneNodes,
  deleteNode,
  parentOptions,
  sortCategoryNodes,
  treesEqual,
  updateNodeName,
  updateNodeNoVat,
} from '../utils/categoryTreeEdit';
import { restoreCategoryTree, saveCategoryTree, fetchCategoryTreeHistory } from '../services/categoryService';
import editorStyles from './ExpenseEditorPage.module.css';
import uploadStyles from './UploadPage.module.css';
import styles from './CategoryTreePage.module.css';

type ExcelOutlineKind = 'plus' | 'minus';
type TreeViewMode = 'outline' | 'orgchart' | 'columns';
type DialTarget = TreeViewMode | 'edit';

const VIEW_OPTIONS: Array<{ id: TreeViewMode; label: string }> = [
  { id: 'outline', label: 'מתאר' },
  { id: 'orgchart', label: 'תרשים' },
  { id: 'columns', label: 'עמודות' },
];

const LEVEL_TITLES = [
  FIELD_LABELS.category1Short,
  FIELD_LABELS.category2Short,
  FIELD_LABELS.category3Short,
];

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.4;
const ZOOM_STEP = 0.15;
const MIN_WINDOW_WIDTH = 420;
const MIN_WINDOW_HEIGHT = 280;
const SIDEBAR_WIDTH = 260;
const PAGE_HEADER_OFFSET = 208;

const TREE_VIEW_STORAGE_KEY = 'categoryTreeViewMode';

const readSavedViewMode = (): TreeViewMode => {
  try {
    const saved = localStorage.getItem(TREE_VIEW_STORAGE_KEY);
    if (saved === 'outline' || saved === 'orgchart' || saved === 'columns') {
      return saved;
    }
  } catch {
    // ignore storage errors
  }
  return 'orgchart';
};

const DIAL_STOPS: Array<{ mode: DialTarget; deg: number; label: string }> = [
  { mode: 'outline', deg: 270, label: 'מתאר' },
  { mode: 'orgchart', deg: 0, label: 'תרשים' },
  { mode: 'columns', deg: 90, label: 'עמודות' },
  { mode: 'edit', deg: 180, label: 'עריכה' },
];

const clockDegFromPoint = (clientX: number, clientY: number, rect: DOMRect) => {
  const dx = clientX - (rect.left + rect.width / 2);
  const dy = clientY - (rect.top + rect.height / 2);
  if (Math.hypot(dx, dy) < 8) return null;
  let deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
  if (deg < 0) deg += 360;
  return deg;
};

const snapClockDeg = (deg: number) => {
  const targets = [0, 90, 180, 270];
  let best = 0;
  let bestDist = Infinity;
  targets.forEach((target) => {
    const dist = Math.min(Math.abs(deg - target), 360 - Math.abs(deg - target));
    if (dist < bestDist) {
      bestDist = dist;
      best = target;
    }
  });
  return best;
};

const targetFromClockDeg = (deg: number): DialTarget => {
  if (deg === 270) return 'outline';
  if (deg === 90) return 'columns';
  if (deg === 180) return 'edit';
  return 'orgchart';
};

const degFromMode = (mode: DialTarget | null) => {
  const stop = DIAL_STOPS.find((item) => item.mode === mode);
  return stop ? stop.deg : 0;
};

const isInteractiveTarget = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest('input, textarea, button, select, a'));
};

const historyHoverText = (item: CategoryTreeHistoryItem) => {
  const date = formatMetricaDate(item.saved_at);
  const summary = (item.summary || '').trim();
  return [date, summary].filter(Boolean).join('\n');
};

const getDefaultWindowRect = () => {
  if (typeof window === 'undefined') {
    return { left: 80, top: 180, width: 800, height: 520 };
  }
  const gap = 24;
  const availLeft = gap;
  const availRight = window.innerWidth - SIDEBAR_WIDTH - gap;
  const availTop = PAGE_HEADER_OFFSET;
  const availBottom = window.innerHeight - gap;
  const availW = Math.max(MIN_WINDOW_WIDTH, availRight - availLeft);
  const availH = Math.max(MIN_WINDOW_HEIGHT, availBottom - availTop);
  const width = Math.min(920, availW);
  const height = Math.min(620, availH);
  return {
    left: availLeft + (availW - width) / 2,
    top: availTop + (availH - height) / 2,
    width,
    height,
  };
};

type WindowRect = { left: number; top: number; width: number; height: number };
type AddPlace = '' | 'root' | '1' | '2';

const getMaximizedWindowRect = (): WindowRect => {
  if (typeof window === 'undefined') {
    return { left: 12, top: 12, width: 960, height: 640 };
  }
  const gap = 12;
  return {
    left: gap,
    top: gap,
    width: Math.max(MIN_WINDOW_WIDTH, window.innerWidth - gap * 2),
    height: Math.max(MIN_WINDOW_HEIGHT, window.innerHeight - gap * 2),
  };
};

const ExcelOutlineIcon: React.FC<{ kind: ExcelOutlineKind }> = ({ kind }) => (
  <svg width="16" height="16" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <rect x="0.5" y="0.5" width="12" height="12" fill="#ececec" stroke="#333333" strokeWidth="1" />
    <path d="M3.5 6.5h6" stroke="#333333" strokeWidth="1.25" />
    {kind === 'plus' && <path d="M6.5 3.5v6" stroke="#333333" strokeWidth="1.25" />}
  </svg>
);

const getAllExpandableIds = (nodes: CategoryNode[]): string[] => {
  const ids: string[] = [];
  nodes.forEach((node) => {
    if (node.children && node.children.length > 0) {
      ids.push(node.id);
      ids.push(...getAllExpandableIds(node.children));
    }
  });
  return ids;
};

const LEVEL_CLASS = [styles.level0, styles.level1, styles.level2];

const CategoryTreePage: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useApp();
  const { categoryTree, auth } = state;
  const { reload } = useCategoryTree();
  const { showError, showConfirmation } = useToast();
  const savedCategories = categoryTree?.categories || [];
  const tenantName = auth.tenant?.name || '';
  const [isEditing, setIsEditing] = useState(false);
  const [draftCategories, setDraftCategories] = useState<CategoryNode[]>([]);
  const [isSavingTree, setIsSavingTree] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyItems, setHistoryItems] = useState<CategoryTreeHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const categories = isEditing ? draftCategories : savedCategories;
  const visibleCategories = useMemo(() => sortCategoryNodes(categories), [categories]);
  const isDirty = isEditing && !treesEqual(draftCategories, savedCategories);
  const savedViewMode = readSavedViewMode();
  const [viewMode, setViewMode] = useState<TreeViewMode>(savedViewMode);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [selectedLevel1Id, setSelectedLevel1Id] = useState<string | null>(null);
  const [selectedLevel2Id, setSelectedLevel2Id] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [windowRect, setWindowRect] = useState(getDefaultWindowRect);
  const [maximized, setMaximized] = useState(false);
  const [regularRect, setRegularRect] = useState<WindowRect | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addPos, setAddPos] = useState({ left: 72, top: 120 });
  const [addName, setAddName] = useState('');
  const [addPlace, setAddPlace] = useState<AddPlace>('');
  const [addParentId, setAddParentId] = useState('');
  const [addNoVat, setAddNoVat] = useState(false);
  const [dialDeg, setDialDeg] = useState(() => degFromMode(savedViewMode));
  const dialRef = useRef<HTMLDivElement>(null);
  const dialDraggingRef = useRef(false);
  const isEditingRef = useRef(false);
  const isDirtyRef = useRef(false);
  const viewModeRef = useRef(viewMode);
  const savedCategoriesRef = useRef(savedCategories);
  const enterEditPendingRef = useRef(false);
  const leaveEditPendingRef = useRef(false);
  const updateDialFromPointerRef = useRef<(clientX: number, clientY: number, snap: boolean) => void>(() => undefined);
  const windowMoveRef = useRef<{ startX: number; startY: number; origLeft: number; origTop: number } | null>(null);
  const windowResizeRef = useRef<{ startX: number; startY: number; origWidth: number; origHeight: number } | null>(null);
  const addMoveRef = useRef<{ startX: number; startY: number; origLeft: number; origTop: number } | null>(null);
  const contentDragRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);

  const resetWindowLayout = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setMaximized(false);
    setRegularRect(null);
    setAddOpen(false);
    setWindowRect(getDefaultWindowRect());
  };

  const maximizeWindow = () => {
    setRegularRect(windowRect);
    setMaximized(true);
    setWindowRect(getMaximizedWindowRect());
  };

  const restoreRegularSize = () => {
    setWindowRect(regularRect || getDefaultWindowRect());
    setRegularRect(null);
    setMaximized(false);
  };

  isEditingRef.current = isEditing;
  isDirtyRef.current = isDirty;
  viewModeRef.current = viewMode;
  savedCategoriesRef.current = savedCategories;

  const openView = (mode: TreeViewMode) => {
    setIsEditing(false);
    setAddOpen(false);
    setDraftCategories([]);
    setViewMode(mode);
    setDialDeg(degFromMode(mode));
    resetWindowLayout();
    try {
      localStorage.setItem(TREE_VIEW_STORAGE_KEY, mode);
    } catch {
      // ignore storage errors
    }
  };

  const beginEdit = () => {
    setDraftCategories(cloneNodes(savedCategoriesRef.current));
    setIsEditing(true);
    setDialDeg(180);
    resetWindowLayout();
  };

  const requestEnterEdit = async () => {
    if (isEditingRef.current || enterEditPendingRef.current) {
      setDialDeg(180);
      return;
    }
    enterEditPendingRef.current = true;
    setDialDeg(180);
    const confirmed = await showConfirmation({
      title: 'עריכת קטגוריות',
      message: 'אתה נכנס לעריכת קטגוריות. שינוי כאן משפיע על עץ הקטגוריות של הפרויקט.',
      confirmText: 'כן',
      cancelText: 'לא',
      type: 'warning',
    });
    enterEditPendingRef.current = false;
    if (confirmed !== 'confirm') {
      setDialDeg(degFromMode(viewModeRef.current));
      return;
    }
    beginEdit();
  };

  const requestOpenView = async (mode: TreeViewMode) => {
    if (!isEditingRef.current) {
      openView(mode);
      return;
    }
    if (leaveEditPendingRef.current) {
      setDialDeg(180);
      return;
    }
    if (isDirtyRef.current) {
      leaveEditPendingRef.current = true;
      const confirmed = await showConfirmation({
        title: 'שינויים שלא נשמרו',
        message: 'לבטל את עריכת העץ בלי לשמור?',
        confirmText: 'כן, בלי לשמור',
        cancelText: 'המשך לערוך',
        type: 'warning',
      });
      leaveEditPendingRef.current = false;
      if (confirmed !== 'confirm') {
        setDialDeg(180);
        return;
      }
    }
    openView(mode);
  };

  const updateDialFromPointer = (clientX: number, clientY: number, snap: boolean) => {
    const rect = dialRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nextDeg = clockDegFromPoint(clientX, clientY, rect);
    if (nextDeg == null) return;
    const applied = snap ? snapClockDeg(nextDeg) : nextDeg;
    setDialDeg(applied);
    if (!snap) return;
    const target = targetFromClockDeg(applied);
    if (target === 'edit') {
      void requestEnterEdit();
    } else {
      void requestOpenView(target);
    }
  };
  updateDialFromPointerRef.current = updateDialFromPointer;

  const recenterTree = () => setPan({ x: 0, y: 0 });
  const collapseAll = () => {
    setExpandedCategories(new Set());
    recenterTree();
  };
  const expandAll = () => {
    setExpandedCategories(new Set(getAllExpandableIds(categories)));
    recenterTree();
  };
  const hasExpandedCategories = expandedCategories.size > 0;

  const clampZoom = (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(value.toFixed(2))));

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      if (dialDraggingRef.current) {
        event.preventDefault();
        updateDialFromPointerRef.current(event.clientX, event.clientY, false);
        return;
      }
      if (addMoveRef.current) {
        const { origLeft, origTop, startX, startY } = addMoveRef.current;
        const left = Math.min(window.innerWidth - 80, Math.max(0, origLeft + (event.clientX - startX)));
        const top = Math.min(window.innerHeight - 40, Math.max(0, origTop + (event.clientY - startY)));
        setAddPos({ left, top });
        return;
      }
      if (windowMoveRef.current) {
        const { origLeft, origTop, startX, startY } = windowMoveRef.current;
        setWindowRect((prev) => ({
          ...prev,
          left: origLeft + (event.clientX - startX),
          top: origTop + (event.clientY - startY),
        }));
        return;
      }
      if (windowResizeRef.current) {
        const { origWidth, origHeight, startX, startY } = windowResizeRef.current;
        setMaximized(false);
        setWindowRect((prev) => ({
          ...prev,
          width: Math.max(MIN_WINDOW_WIDTH, origWidth + (event.clientX - startX)),
          height: Math.max(MIN_WINDOW_HEIGHT, origHeight + (event.clientY - startY)),
        }));
        return;
      }
      if (contentDragRef.current) {
        const { origX, origY, startX, startY } = contentDragRef.current;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (Math.hypot(dx, dy) > 4) contentDragRef.current.moved = true;
        setPan({ x: origX + dx, y: origY + dy });
      }
    };
    const onPointerUp = (event: PointerEvent) => {
      if (dialDraggingRef.current) {
        dialDraggingRef.current = false;
        updateDialFromPointerRef.current(event.clientX, event.clientY, true);
      }
      if (contentDragRef.current?.moved) {
        suppressClickRef.current = true;
      }
      windowMoveRef.current = null;
      windowResizeRef.current = null;
      addMoveRef.current = null;
      contentDragRef.current = null;
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, []);

  const getAllDescendantIds = (category: CategoryNode): string[] => {
    const ids: string[] = [];
    if (category.children) {
      category.children.forEach((child) => {
        ids.push(child.id);
        ids.push(...getAllDescendantIds(child));
      });
    }
    return ids;
  };

  const toggleCategory = (category: CategoryNode) => {
    const next = new Set(expandedCategories);
    if (next.has(category.id)) {
      next.delete(category.id);
      getAllDescendantIds(category).forEach((id) => next.delete(id));
    } else {
      next.add(category.id);
    }
    setExpandedCategories(next);
  };

  const cancelEdit = async () => {
    if (isDirty) {
      const confirmed = await showConfirmation({
        title: 'שינויים שלא נשמרו',
        message: 'לבטל את עריכת העץ בלי לשמור?',
        confirmText: 'כן, בלי לשמור',
        cancelText: 'המשך לערוך',
        type: 'warning',
      });
      if (confirmed !== 'confirm') return;
    }
    openView(viewMode);
  };

  const handleSaveTree = async () => {
    if (!isDirty) {
      openView(viewMode);
      return;
    }
    setIsSavingTree(true);
    try {
      await saveCategoryTree({
        version: categoryTree?.version || '2.0',
        categories: draftCategories,
      });
      await reload();
      openView(viewMode);
    } catch (error) {
      showError('שגיאה בשמירת עץ הקטגוריות', {
        action: 'Save category tree',
        error,
      });
    } finally {
      setIsSavingTree(false);
    }
  };

  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      setHistoryItems(await fetchCategoryTreeHistory());
    } catch (error) {
      showError('שגיאה בטעינת היסטוריית העץ', {
        action: 'Load category tree history',
        error,
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRestore = async (item: CategoryTreeHistoryItem) => {
    const confirmed = await showConfirmation({
      title: 'שחזור עץ קטגוריות',
      message: 'לשחזר את העץ לגרסה זו? הגרסה הנוכחית תישמר בהיסטוריה.',
      confirmText: 'שחזר',
      cancelText: 'ביטול',
      type: 'warning',
    });
    if (!confirmed) return;
    setIsSavingTree(true);
    try {
      await restoreCategoryTree(item.id);
      await reload();
      setShowHistory(false);
      openView(viewMode);
    } catch (error) {
      showError('שגיאה בשחזור עץ הקטגוריות', {
        action: 'Restore category tree',
        error,
      });
    } finally {
      setIsSavingTree(false);
    }
  };

  const selectedLevel1 = visibleCategories.find((category) => category.id === selectedLevel1Id) || null;
  const selectedLevel2 =
    selectedLevel1?.children?.find((category) => category.id === selectedLevel2Id) || null;

  const renderOutlineCategory = (
    category: CategoryNode,
    level: number = 0,
    inheritedNoVat: boolean = false
  ) => {
    const isExpanded = expandedCategories.has(category.id);
    const hasChildren = Boolean(category.children && category.children.length > 0);
    const levelClass = LEVEL_CLASS[level] || styles.level2;
    const isNoVat = inheritedNoVat || Boolean(category.no_vat);
    const inheritedForChildren = isNoVat;

    return (
      <div key={category.id} className={styles.outlineItem}>
        <div className={`${styles.outlineRow} ${levelClass}`}>
          <button
            type="button"
            className={`${styles.outlineToggle} ${hasChildren ? styles.clickable : ''}`}
            onClick={() => hasChildren && toggleCategory(category)}
            aria-label={hasChildren ? (isExpanded ? 'כווץ' : 'הרחב') : category.name}
          >
            {hasChildren ? (
              <span className={styles.outlineMarker} aria-hidden>
                {isExpanded ? '−' : '+'}
              </span>
            ) : (
              <span className={styles.outlineMarkerDot} aria-hidden />
            )}
          </button>
          {isEditing ? (
            <input
              className={styles.outlineNameInput}
              value={category.name}
              onChange={(event) =>
                setDraftCategories((prev) => updateNodeName(prev, category.id, event.target.value))
              }
              aria-label={`שם קטגוריה רמה ${level + 1}`}
            />
          ) : (
            <button
              type="button"
              className={`${styles.outlineNameButton} ${hasChildren ? styles.clickable : ''}`}
              onClick={() => hasChildren && toggleCategory(category)}
            >
              <span className={styles.categoryName}>{category.name}</span>
            </button>
          )}
          {isNoVat && !isEditing && (
            <span className={styles.noVatBadge} title={inheritedNoVat ? 'ירושה מהקטגוריה שמעליה' : 'ללא מע״מ'}>
              ללא מע״מ
            </span>
          )}
          {isEditing && (
            <div className={styles.outlineActions}>
              <label
                className={`${styles.noVatCheck} ${inheritedNoVat ? styles.noVatCheckInherited : ''}`}
                title={inheritedNoVat ? 'ירושה מהקטגוריה שמעליה' : 'ללא מע״מ'}
              >
                <input
                  type="checkbox"
                  checked={isNoVat}
                  disabled={inheritedNoVat}
                  onChange={(event) =>
                    setDraftCategories((prev) => updateNodeNoVat(prev, category.id, event.target.checked))
                  }
                />
                ללא מע״מ
              </label>
              <button
                type="button"
                className={styles.treeActionButton}
                onClick={async () => {
                  const confirmed = await showConfirmation({
                    title: 'מחיקת קטגוריה',
                    message: 'למחוק את הקטגוריה ואת כל התת־קטגוריות שלה? הוצאות קיימות לא ישתנו.',
                    confirmText: 'מחק',
                    cancelText: 'ביטול',
                    type: 'danger',
                  });
                  if (!confirmed) return;
                  setDraftCategories((prev) => deleteNode(prev, category.id));
                }}
                title="מחק"
              >
                ×
              </button>
            </div>
          )}
        </div>
        {hasChildren && isExpanded && category.children && (
          <div className={styles.outlineChildren}>
            {category.children.map((child) =>
              renderOutlineCategory(child, level + 1, inheritedForChildren)
            )}
          </div>
        )}
      </div>
    );
  };

  const renderOrgChartCategory = (
    category: CategoryNode,
    level: number = 0,
    inheritedNoVat: boolean = false
  ) => {
    const isExpanded = expandedCategories.has(category.id);
    const hasChildren = Boolean(category.children && category.children.length > 0);
    const levelClass = LEVEL_CLASS[level] || styles.level2;
    const isNoVat = inheritedNoVat || Boolean(category.no_vat);

    return (
      <div key={category.id} className={styles.chartNode}>
        <button
          type="button"
          className={`${styles.chartLabel} ${levelClass} ${hasChildren ? styles.clickable : ''}`}
          onClick={() => hasChildren && toggleCategory(category)}
        >
          <span className={styles.categoryName}>{category.name}</span>
          {isNoVat && (
            <span className={styles.noVatBadge} title={inheritedNoVat ? 'ירושה מהקטגוריה שמעליה' : 'ללא מע״מ'}>
              ללא מע״מ
            </span>
          )}
        </button>
        {hasChildren && isExpanded && category.children && (
          <div className={styles.chartChildren}>
            {category.children.map((child) => renderOrgChartCategory(child, level + 1, isNoVat))}
          </div>
        )}
      </div>
    );
  };

  const renderColumnItems = (
    items: CategoryNode[],
    level: number,
    selectedId: string | null,
    onSelect: (category: CategoryNode) => void,
    inheritedNoVat: boolean = false
  ) => {
    const levelClass = LEVEL_CLASS[level] || styles.level2;
    if (items.length === 0) {
      return <div className={styles.columnEmpty}>אין קטגוריות</div>;
    }

    return items.map((item) => {
      const hasChildren = Boolean(item.children && item.children.length > 0);
      const canSelect = hasChildren || level === 2;
      const isNoVat = inheritedNoVat || Boolean(item.no_vat);
      return (
        <button
          key={item.id}
          type="button"
          className={`${styles.columnItem} ${levelClass} ${selectedId === item.id ? styles.columnItemSelected : ''} ${canSelect ? styles.clickable : ''}`}
          onClick={() => canSelect && onSelect(item)}
        >
          <span className={styles.categoryName}>{item.name}</span>
          {isNoVat && (
            <span className={styles.noVatBadge} title={inheritedNoVat ? 'ירושה מהקטגוריה שמעליה' : 'ללא מע״מ'}>
              ללא מע״מ
            </span>
          )}
        </button>
      );
    });
  };

  const renderActiveView = () => {
    if (isEditing) {
      return (
        <div className={styles.outlineList}>
          {visibleCategories.map((category) => renderOutlineCategory(category))}
        </div>
      );
    }
    if (visibleCategories.length === 0) {
      return (
        <div className={styles.emptyTreeHint}>
          אין עץ קטגוריות שמור. היכנסו לעריכה כדי ליצור עץ חדש.
        </div>
      );
    }
    if (viewMode === 'outline') {
      return (
        <div className={styles.outlineList}>
          {visibleCategories.map((category) => renderOutlineCategory(category))}
        </div>
      );
    }
    if (viewMode === 'orgchart') {
      return (
        <div className={styles.chartList}>
          {visibleCategories.map((category) => renderOrgChartCategory(category))}
        </div>
      );
    }
    return (
      <div className={styles.columnsLayout}>
        <section className={styles.column} aria-label={LEVEL_TITLES[0]}>
          <h2 className={`${styles.columnTitle} ${styles.level0}`}>{LEVEL_TITLES[0]}</h2>
          <div className={styles.columnBody}>
            {renderColumnItems(visibleCategories, 0, selectedLevel1Id, (category) => {
              setSelectedLevel1Id(category.id);
              setSelectedLevel2Id(null);
            })}
          </div>
        </section>
        <section className={styles.column} aria-label={LEVEL_TITLES[1]}>
          <h2 className={`${styles.columnTitle} ${styles.level1}`}>{LEVEL_TITLES[1]}</h2>
          <div className={styles.columnBody}>
            {selectedLevel1?.children?.length
              ? renderColumnItems(selectedLevel1.children, 1, selectedLevel2Id, (category) => {
                  setSelectedLevel2Id(category.id);
                }, Boolean(selectedLevel1.no_vat))
              : <div className={styles.columnEmpty}>בחרו {LEVEL_TITLES[0]}</div>}
          </div>
        </section>
        <section className={styles.column} aria-label={LEVEL_TITLES[2]}>
          <h2 className={`${styles.columnTitle} ${styles.level2}`}>{LEVEL_TITLES[2]}</h2>
          <div className={styles.columnBody}>
            {selectedLevel2?.children?.length
              ? renderColumnItems(
                  selectedLevel2.children,
                  2,
                  null,
                  () => undefined,
                  Boolean(selectedLevel1?.no_vat || selectedLevel2.no_vat)
                )
              : <div className={styles.columnEmpty}>בחרו {selectedLevel1 ? LEVEL_TITLES[1] : LEVEL_TITLES[0]}</div>}
          </div>
        </section>
      </div>
    );
  };

  const activeViewLabel = isEditing ? 'עריכה' : VIEW_OPTIONS.find((option) => option.id === viewMode)?.label || '';

  const closeAdd = () => {
    setAddOpen(false);
    setAddName('');
    setAddPlace('');
    setAddParentId('');
    setAddNoVat(false);
  };

  const openAdd = () => {
    setAddName('');
    setAddPlace('');
    setAddParentId('');
    setAddNoVat(false);
    setAddPos({ left: 72, top: 120 });
    setAddOpen(true);
  };

  const applyAdd = () => {
    const name = addName.trim();
    if (!name || !addPlace) return;
    if (addPlace === 'root') {
      setDraftCategories((prev) => addNamedChild(prev, null, name, addNoVat));
      closeAdd();
      return;
    }
    if (!addParentId) return;
    const parentId = addParentId;
    setDraftCategories((prev) => addNamedChild(prev, parentId, name, addNoVat));
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      (ancestorIds(draftCategories, parentId) || []).forEach((id) => next.add(id));
      next.add(parentId);
      return next;
    });
    closeAdd();
  };

  const addParents = addPlace === '1' || addPlace === '2'
    ? parentOptions(draftCategories, addPlace === '1' ? 1 : 2)
    : [];
  const canApplyAdd = Boolean(addName.trim() && addPlace && (addPlace === 'root' || addParentId));

  const popup = createPortal(
    <div
      className={`${styles.popupWindow} ${isEditing ? styles.popupWindowEdit : ''}`}
      role="region"
      aria-label={activeViewLabel}
      style={{
        left: windowRect.left,
        top: windowRect.top,
        width: windowRect.width,
        height: windowRect.height,
      }}
    >
      <header
        className={styles.popupHeader}
        onPointerDown={(event) => {
          if (maximized) return;
          event.preventDefault();
          windowMoveRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            origLeft: windowRect.left,
            origTop: windowRect.top,
          };
        }}
        onDoubleClick={(event) => {
          event.preventDefault();
          if (maximized) restoreRegularSize();
          else maximizeWindow();
        }}
      >
        <div className={styles.popupHeaderStart}>
          {isEditing && (
            <>
              <button
                type="button"
                className={styles.popupSaveButton}
                onPointerDown={(event) => event.stopPropagation()}
                onDoubleClick={(event) => event.stopPropagation()}
                onClick={handleSaveTree}
                disabled={isSavingTree || !isDirty}
              >
                {isSavingTree ? 'שומר...' : 'שמור'}
              </button>
              <button
                type="button"
                className={styles.popupHistoryButton}
                onPointerDown={(event) => event.stopPropagation()}
                onDoubleClick={(event) => event.stopPropagation()}
                onClick={openHistory}
                disabled={isSavingTree}
              >
                היסטוריה
              </button>
              <button
                type="button"
                className={styles.popupHistoryButton}
                onPointerDown={(event) => event.stopPropagation()}
                onDoubleClick={(event) => event.stopPropagation()}
                onClick={openAdd}
              >
                הוסף קטגוריה
              </button>
            </>
          )}
        </div>
        <h2>{activeViewLabel}</h2>
        <div className={styles.popupHeaderEnd}>
          {regularRect && (
            <button
              type="button"
              className={styles.popupHistoryButton}
              onPointerDown={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()}
              onClick={restoreRegularSize}
            >
              גודל רגיל
            </button>
          )}
          {(isEditing || viewMode !== 'columns') && (
            <button
              type="button"
              className={styles.headerOutlineButton}
              onPointerDown={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()}
              onClick={() => (hasExpandedCategories ? collapseAll() : expandAll())}
              title={hasExpandedCategories ? 'סגור הכל' : 'פתח את כל הרמות'}
              aria-label={hasExpandedCategories ? 'סגור הכל' : 'פתח את כל הרמות'}
            >
              <ExcelOutlineIcon kind={hasExpandedCategories ? 'minus' : 'plus'} />
            </button>
          )}
        </div>
      </header>
      <div
        className={styles.popupViewport}
        onWheel={(event) => {
          event.preventDefault();
          const direction = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
          setZoom((z) => clampZoom(z + direction));
        }}
        onPointerDown={(event) => {
          if (isInteractiveTarget(event.target)) return;
          contentDragRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            origX: pan.x,
            origY: pan.y,
            moved: false,
          };
        }}
        onClickCapture={(event) => {
          if (!suppressClickRef.current) return;
          event.preventDefault();
          event.stopPropagation();
          suppressClickRef.current = false;
        }}
      >
        <div
          className={styles.popupCanvas}
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
        >
          {renderActiveView()}
        </div>
      </div>
      <div
        className={styles.resizeHandle}
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          windowResizeRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            origWidth: windowRect.width,
            origHeight: windowRect.height,
          };
        }}
        aria-hidden
      />
    </div>,
    document.body
  );

  return (
    <div className={editorStyles.container}>
      <div className={`${uploadStyles.uploadHeader} ${styles.pageHeader}`}>
        <div className={`${editorStyles.headerTitleRow} ${uploadStyles.uploadHeaderTitleRow}`}>
          <h1>מבנה קטגוריות</h1>
        </div>
        <div className={styles.vaultDialWrap} role="group" aria-label="בחירת תצוגת עץ">
          <span className={`${styles.vaultLabel} ${styles.vaultLabel12} ${!isEditing && viewMode === 'orgchart' ? styles.vaultLabelActive : ''}`}>
            תרשים
          </span>
          <span className={`${styles.vaultLabel} ${styles.vaultLabel9} ${!isEditing && viewMode === 'outline' ? styles.vaultLabelActive : ''}`}>
            מתאר
          </span>
          <span className={`${styles.vaultLabel} ${styles.vaultLabel3} ${!isEditing && viewMode === 'columns' ? styles.vaultLabelActive : ''}`}>
            עמודות
          </span>
          <span className={`${styles.vaultLabel} ${styles.vaultLabel6} ${isEditing ? styles.vaultLabelActive : ''}`}>
            עריכה
          </span>
          <div
            ref={dialRef}
            className={styles.vaultDial}
            onPointerDown={(event) => {
              event.preventDefault();
              dialDraggingRef.current = true;
              updateDialFromPointer(event.clientX, event.clientY, false);
            }}
          >
            {Array.from({ length: 12 }, (_, hour) => (
              <span
                key={hour}
                className={`${styles.vaultHourMark} ${hour === 0 || hour === 3 || hour === 6 || hour === 9 ? styles.vaultHourMarkMajor : ''}`}
                style={{ transform: `rotate(${hour * 30}deg)` }}
                aria-hidden
              />
            ))}
            <div className={styles.vaultFace} aria-hidden />
            <div className={styles.vaultRotor} style={{ transform: `rotate(${dialDeg}deg)` }}>
              <button
                type="button"
                className={styles.vaultInnerButton}
                aria-label="חוגת בחירת תצוגה"
                title="גררו לבחירת תצוגה"
              />
            </div>
          </div>
        </div>
        <div className={`${editorStyles.headerActions} ${styles.treeHeaderActions}`}>
          {isEditing && (
            <button type="button" className={editorStyles.backButton} onClick={cancelEdit} disabled={isSavingTree}>
              ביטול
            </button>
          )}
          {!isEditing && (
            <button type="button" className={editorStyles.backButton} onClick={openHistory} disabled={isSavingTree}>
              היסטוריה
            </button>
          )}
          <button type="button" className={editorStyles.backButton} onClick={() => navigate('/')}>
            ← חזור לרשימה
          </button>
          <button
            type="button"
            className={styles.exportIconButton}
            onClick={() => printCategoryTreePdf(visibleCategories, tenantName, formatMetricaDate(new Date().toISOString()))}
            title="ייצוא ל־PDF"
            aria-label="ייצוא ל־PDF"
          >
            <svg width="23" height="23" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 2h8l6 6v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" fill="#E53935" />
              <path d="M14 2v6h6" fill="#FFCDD2" />
              <text x="12" y="17.5" textAnchor="middle" fill="#fff" fontSize="6.5" fontWeight="700" fontFamily="Arial, sans-serif">
                PDF
              </text>
            </svg>
          </button>
          <button
            type="button"
            className={styles.exportIconButton}
            onClick={() => downloadCategoryTreeExcel(visibleCategories, new Date().toISOString().slice(0, 10))}
            title="ייצוא ל־Excel"
            aria-label="ייצוא ל־Excel"
          >
            <img
              src={`${process.env.PUBLIC_URL}/excel-icon.png`}
              alt=""
              width={23}
              height={23}
              aria-hidden="true"
              draggable={false}
            />
          </button>
        </div>
      </div>
      {popup}
      {addOpen && createPortal(
        <div
          className={styles.addFloat}
          role="dialog"
          aria-label="הוסף קטגוריה"
          style={{ left: addPos.left, top: addPos.top }}
        >
          <header
            className={styles.addFloatHeader}
            onPointerDown={(event) => {
              if (event.target instanceof Element && event.target.closest('button')) return;
              event.preventDefault();
              addMoveRef.current = {
                startX: event.clientX,
                startY: event.clientY,
                origLeft: addPos.left,
                origTop: addPos.top,
              };
            }}
          >
            <button type="button" className={styles.addFloatClose} onClick={closeAdd} aria-label="סגור">
              סגור
            </button>
            <h3>הוסף קטגוריה</h3>
          </header>
          <form
            className={styles.addFloatBody}
            onSubmit={(event) => {
              event.preventDefault();
              applyAdd();
            }}
          >
            <label className={styles.addField}>
              <span>שם</span>
              <input
                className={styles.addInput}
                value={addName}
                onChange={(event) => setAddName(event.target.value)}
                autoFocus
              />
            </label>
            <fieldset className={styles.addLevels}>
              <legend>רמה</legend>
              <label>
                <input
                  type="radio"
                  name="add-place"
                  checked={addPlace === 'root'}
                  onChange={() => {
                    setAddPlace('root');
                    setAddParentId('');
                  }}
                />
                קטגוריה ראשית
              </label>
              <label>
                <input
                  type="radio"
                  name="add-place"
                  checked={addPlace === '1'}
                  onChange={() => {
                    setAddPlace('1');
                    setAddParentId('');
                  }}
                />
                תחת רמה 1
              </label>
              <label>
                <input
                  type="radio"
                  name="add-place"
                  checked={addPlace === '2'}
                  onChange={() => {
                    setAddPlace('2');
                    setAddParentId('');
                  }}
                />
                תחת רמה 2
              </label>
            </fieldset>
            {(addPlace === '1' || addPlace === '2') && (
              <label className={styles.addField}>
                <span>תחת</span>
                <select
                  className={styles.addInput}
                  value={addParentId}
                  onChange={(event) => setAddParentId(event.target.value)}
                >
                  <option value="">בחרו קטגוריה</option>
                  {addParents.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className={styles.addCheck}>
              <input
                type="checkbox"
                checked={addNoVat}
                onChange={(event) => setAddNoVat(event.target.checked)}
              />
              ללא מע״מ
            </label>
            <button type="submit" className={styles.addApply} disabled={!canApplyAdd}>
              החל
            </button>
          </form>
        </div>,
        document.body,
      )}
      {showHistory && (
        <div className={styles.historyBackdrop} role="dialog" aria-label="היסטוריית עץ קטגוריות">
          <div className={styles.historyPanel}>
            <div className={styles.historyHeader}>
              <h2>היסטוריית שינויים</h2>
              <button type="button" className={editorStyles.backButton} onClick={() => setShowHistory(false)}>
                סגור
              </button>
            </div>
            {historyLoading ? (
              <p className={styles.historyEmpty}>טוען...</p>
            ) : historyItems.length === 0 ? (
              <p className={styles.historyEmpty}>אין גרסאות שמורות עדיין</p>
            ) : (
              <ul className={styles.historyList}>
                {historyItems.map((item) => (
                  <li key={item.id} className={styles.historyItem} title={historyHoverText(item)}>
                    <div>
                      <div className={styles.historyDate}>
                        <bdi>{formatMetricaDate(item.saved_at)}</bdi>
                      </div>
                      <div className={styles.historyMeta}>
                        {item.saved_by_name || item.saved_by_email || ''}
                        {item.action === 'restore' ? ' · שחזור' : ' · שמירה'}
                      </div>
                      <div className={styles.historyHoverCard}>
                        <div className={styles.historyHoverDate}>
                          <bdi>{formatMetricaDate(item.saved_at)}</bdi>
                        </div>
                        <div>{item.summary || 'אין פירוט שינויים'}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={editorStyles.saveButton}
                      onClick={() => handleRestore(item)}
                      disabled={isSavingTree}
                    >
                      שחזר
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryTreePage;
